/* ──────────────────────────────────────────────────────────────
   SessionContext.tsx · the real signed-in user/org from platform /v1/me
   Replaces all hardcoded "Priya Raman / Madras Filter Co." mock identity.
   ────────────────────────────────────────────────────────────── */

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { getCurrentUser } from '../services/auth/auth';
import { Token_Handler } from '../storage/tokenHandler';
import { roleForDisplay, isSoftknackStaff } from './roleForDisplay';

export interface SessionUser {
  id?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
}
export interface SessionOrg {
  id?: string;
  name?: string;
  /** CH-U1 — the slug participates in resolution (GET /v1/internal/resolve?slug=). */
  slug?: string;
  /** 'inactive' | 'active' | 'suspended'. Only 'active' resolves for riders. */
  status?: string;
}

interface SessionValue {
  user: SessionUser | null;
  org: SessionOrg | null;
  role: string | null;
  /** True for SOFTKNACK's own people (not a tenant's employees) — gates the
   *  Admin Console. Sourced from /v1/me, never from a decoded JWT. */
  isPlatformAdmin: boolean;
  loading: boolean;
  fullName: string;
  refresh: () => Promise<void>;
}

const SessionContext = createContext<SessionValue>({
  user: null, org: null, role: null, isPlatformAdmin: false, loading: true, fullName: '', refresh: async () => {},
});

// Maps the platform role slug to a human label for the sidebar.
function roleLabel(role: string | null): string {
  switch (role) {
    case 'org_admin': return 'Owner';
    case 'admin': return 'Admin';
    case 'platform_admin': return 'Platform Admin';
    case 'user': return 'Member';
    default: return role ? role.replace(/_/g, ' ') : '';
  }
}

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [org, setOrg] = useState<SessionOrg | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  // Ensures the "unauthenticated → wipe stale display metadata" cleanup runs at
  // most once (on the first failed load), never on later transient refreshes.
  const cleanedRef = useRef(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await getCurrentUser();
      const d = res?.data ?? res;
      setUser(d?.user ?? null);
      setOrg(d?.org ?? null);
      // CH-U6 — was `memberships[0].role`, i.e. whichever membership the API
      // happened to return first. For a multi-org person that badge was
      // arbitrary and could change between reloads. roleForDisplay picks the
      // CURRENT org's membership, falling back to highest privilege.
      setRole(roleForDisplay(d));
      setIsPlatformAdmin(isSoftknackStaff(d));
    } catch {
      setUser(null); setOrg(null); setRole(null); setIsPlatformAdmin(false);
      // No valid session on boot → clear any leftover sk_<product>_* display
      // data so an already-polluted browser (see the stale localStorage keys)
      // starts fresh. Once only, so a mid-session refresh blip never wipes it.
      if (!cleanedRef.current) {
        cleanedRef.current = true;
        Token_Handler.clearAll().catch(() => { /* non-critical */ });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim();

  return (
    <SessionContext.Provider value={{ user, org, role, isPlatformAdmin, loading, fullName, refresh }}>
      {children}
    </SessionContext.Provider>
  );
}

export const useSession = () => useContext(SessionContext);
export { roleLabel };
